// apps/web/src/features/navigation/Footer/components/FooterNewsletter/index.tsx
"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useSubscribe } from "@trydig/centra/client";
import { clsx } from "@trydig/ui";
import { type ComponentProps, useState, useTransition } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { ActionInput } from "@/components/ui/ActionInput";
import { Alert } from "@/components/ui/Alert";
import { Typography } from "@/components/ui/Typography";
import type { StoriesRoute } from "@/data/routes/stories.route";
import { useTranslations } from "@/i18n/client";
import { type NewsletterInput, newsletterSignupSchema } from "./schema";

export interface FooterNewsletterProps extends Omit<ComponentProps<"form">, "onSubmit"> {
	header: NonNullable<StoriesRoute["footer"]>["newsletterHeader"];
	description: NonNullable<StoriesRoute["footer"]>["newsletterDescription"];
	placeholder: NonNullable<StoriesRoute["footer"]>["newsletterPlaceholder"];
}

export const FooterNewsletter = ({
	header,
	description,
	placeholder,
	className,
	...props
}: FooterNewsletterProps) => {
	const subscribe = useSubscribe();
	const t = useTranslations("footer.newsletter");
	// tRaw resolves fully-qualified keys returned by the schema / server action
	// (e.g. "footer.newsletter.fieldError.emailInvalid"). It's only called with
	// a variable at runtime, so the static references below are what
	// push-translations.ts picks up:
	//
	//   tRaw("footer.newsletter.fieldError.emailRequired")
	//   tRaw("footer.newsletter.fieldError.emailInvalid")
	const tRaw = useTranslations();
	const [success, setSuccess] = useState<boolean | null>(null);
	const [error, setError] = useState<boolean | null>(null);
	const [isPending, startTransition] = useTransition();

	const {
		register,
		handleSubmit,
		clearErrors,
		reset,
		formState: { errors },
	} = useForm<NewsletterInput>({
		resolver: zodResolver(newsletterSignupSchema),
	});

	const onSubmit: SubmitHandler<NewsletterInput> = ({ email }) => {
		setSuccess(null);
		startTransition(async () => {
			subscribe.mutate(
				{ email },
				{
					onSuccess: () => {
						reset();
						setSuccess(true);
					},
					onError: () => {
						setError(true);
					},
				},
			);
		});
	};

	// Schema messages are fully-qualified translation keys; tRaw resolves them
	// (and returns translated strings unchanged thanks to the messages[key] ??
	// key fallback in @trydig/next).
	const inlineError = errors.email?.message ? tRaw(errors.email.message) : undefined;

	return (
		<div className={clsx("flex flex-col gap-2 text-text-sand-text-3", className)}>
			<form className="flex flex-col gap-2" onSubmit={handleSubmit(onSubmit)} {...props}>
				<Typography variant="label-sm-reg">{header}</Typography>
				<ActionInput
					{...register("email", { required: true })}
					placeholder={placeholder}
					error={inlineError}
					disabled={isPending}
				/>
				<Typography variant="body-xs-reg" className="leading-[150%] md:max-w-[45%] lg:max-w-[none]">
					{description}
				</Typography>
			</form>
			{inlineError && (
				<Alert
					variant="error"
					closable
					onClose={() => clearErrors("email")}
					closeLabel={t("alert.closeLabel")}
					title={t("validation.title")}
					description={inlineError}
				/>
			)}

			{success && (
				<Alert
					variant="success"
					closable
					onClose={() => setSuccess(null)}
					closeLabel={t("alert.closeLabel")}
					title={t("success.title")}
					description={t("success.description")}
				/>
			)}
			{error && (
				<Alert
					variant="error"
					closable
					onClose={() => setError(null)}
					closeLabel={t("alert.closeLabel")}
					title={t("error.title")}
					description={t("error.description")}
				/>
			)}
		</div>
	);
};
